/**
 * Question Panel Component
 * Handles all question types: text input, multiple choice, number input, item selection
 * Enhanced with pre-question context narratives
 */

import React, { useState } from 'react';
import { Question, AnswerValue } from '../models/Question';
import { InventoryItem } from '../models/Inventory';
import InventoryDisplay from './InventoryDisplay';
import { getCharacterIcon } from '@/config/playerIcons';
import { IoChatbubble, IoWarning, IoBulb, IoCheckmark } from 'react-icons/io5';

interface QuestionContext {
  speaker: 'NARRATOR' | 'MOTHERSHIP' | 'GEE' | 'DOC';
  text: string;
}

interface QuestionPanelProps {
  question: Question;
  inventory?: InventoryItem[];
  maxValue?: number;
  onSubmit: (answer: AnswerValue) => void;
  disabled?: boolean;
  currentAnswer?: AnswerValue;
  className?: string;
  questionContext?: QuestionContext;
  postDecisionFeedback?: string;
}

export const QuestionPanel: React.FC<QuestionPanelProps> = ({
  question,
  inventory = [],
  maxValue,
  onSubmit,
  disabled = false,
  currentAnswer,
  className = '',
  questionContext,
  postDecisionFeedback
}) => {
  const [answer, setAnswer] = useState<AnswerValue>(currentAnswer || '');
  const [error, setError] = useState<string>('');

  const getSpeakerColor = (speaker: string): string => {
    const colors: Record<string, string> = {
      MOTHERSHIP: '#12356B',
      NARRATOR: '#DD6832',
      GEE: '#367A25',
      DOC: '#12356B'
    };
    return colors[speaker] || '#888888';
  };

  const getSpeakerIcon = (speaker: string): React.ReactNode => {
    const iconUrl = getCharacterIcon(speaker);
    if (iconUrl) {
      return <img src={iconUrl} alt={speaker} className="w-8 h-8 object-contain" />;
    }
    return <IoChatbubble />;
  };

  const handleSubmit = () => {
    // Validate answer
    if (question.type === 'text_input' && !answer) {
      setError('Please enter an answer');
      return;
    }

    if (question.type === 'multiple_choice' && !answer) {
      setError('Please select an option');
      return;
    }

    if (question.type === 'number_input') {
      const num = Number(answer);
      if (isNaN(num)) {
        setError('Please enter a valid number');
        return;
      }
      if (question.min !== undefined && num < question.min) {
        setError(`Minimum value is ${question.min}`);
        return;
      }
      if (maxValue !== undefined && num > maxValue) {
        setError(`Maximum value is ${maxValue}`);
        return;
      }
    }

    if (question.type === 'item_selection' && !answer) {
      setError('Please select an item');
      return;
    }

    setError('');
    onSubmit(answer);
  };

  const renderTextInput = () => (
    <div className="mb-4">
      <input
        type="text"
        value={answer as string}
        onChange={(e) => setAnswer(e.target.value)}
        disabled={disabled}
        placeholder="Enter your answer..."
        className="w-full px-4 py-3 border border-[#CED4DE] rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#12356B]/30 focus:border-[#12356B] disabled:bg-gray-100 disabled:cursor-not-allowed"
      />
    </div>
  );

  const renderMultipleChoice = () => (
    <div className="space-y-3 mb-4">
      {question.options?.map((option) => {
        let optionClasses = 'border rounded-lg p-4 cursor-pointer transition-all ';
        if (answer === option.id) {
          optionClasses += 'border-[#12356B] bg-[#E2E5EC] shadow-sm';
        } else {
          optionClasses += 'border-[#CED4DE] bg-white hover:border-[#12356B]/50 hover:shadow-sm';
        }
        if (disabled) optionClasses += ' opacity-50 cursor-not-allowed';

        return (
          <div
            key={option.id}
            className={optionClasses}
            onClick={() => !disabled && setAnswer(option.id)}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                checked={answer === option.id}
                onChange={() => setAnswer(option.id)}
                disabled={disabled}
                className="mt-1 w-4 h-4 text-[#12356B] focus:ring-[#12356B]"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-700 mb-1">Option {option.id}</div>
                <div className="text-sm text-black">{option.label}</div>
                {option.description && (
                  <div className="text-xs text-slate-500 mt-1">{option.description}</div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderNumberInput = () => (
    <div className="mb-4">
      <div className="flex items-center gap-4 mb-3">
        <input
          type="number"
          value={answer as number}
          onChange={(e) => setAnswer(Number(e.target.value))}
          disabled={disabled}
          min={question.min}
          max={maxValue}
          placeholder="Enter number..."
          className="flex-1 px-4 py-3 border border-[#CED4DE] rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#12356B]/30 focus:border-[#12356B] disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        <div className="text-sm text-gray-600 whitespace-nowrap">
          Range: {question.min || 0} - {maxValue || '?'}
        </div>
      </div>
      {question.isolated && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 flex items-start gap-2">
          <span className="text-xl"><IoWarning /></span>
          <span className="text-sm text-yellow-800">This decision is private. Other players cannot see your choice.</span>
        </div>
      )}
    </div>
  );

  const renderItemSelection = () => (
    <div className="mb-4">
      <p className="text-sm text-gray-700 mb-3">
        Select one item from your inventory:
      </p>
      <InventoryDisplay
        inventory={inventory}
        selectable
        selectedItem={answer as string}
        onItemClick={(item) => setAnswer(item.id)}
      />
    </div>
  );

  return (
    <div className={className}>
      {/* Pre-Question Context */}
      {questionContext && (
        <div
          className="bg-white border-l-4 border-gray-200 rounded-xl shadow-sm mb-4 overflow-hidden"
          style={{ borderLeftColor: getSpeakerColor(questionContext.speaker) }}
        >
          <div
            className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200"
          >
            <span className="text-2xl">{getSpeakerIcon(questionContext.speaker)}</span>
            <span
              className="text-lg font-semibold"
              style={{ color: getSpeakerColor(questionContext.speaker) }}
            >
              {questionContext.speaker}
            </span>
          </div>
          <div className="px-6 py-5">
            <p className="text-gray-800 text-base leading-relaxed">{questionContext.text}</p>
          </div>
        </div>
      )}

      {/* Question Panel */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col gap-3">
            <h3 className="text-xl font-bold text-black">{question.question}</h3>
            <div className="flex flex-wrap gap-2">
              {question.individual && (
                <span className="px-3 py-1 bg-[#DD6832]/10 text-[#DD6832] text-xs font-semibold rounded-lg">
                  Individual Decision
                </span>
              )}
              {question.requiresConsensus && (
                <span className="px-3 py-1 bg-[#367A25]/10 text-[#367A25] text-xs font-semibold rounded-lg">
                  Requires Consensus
                </span>
              )}
            </div>
          </div>
        </div>

      <div className="px-6 py-6">
        {question.type === 'text_input' && renderTextInput()}
        {question.type === 'multiple_choice' && renderMultipleChoice()}
        {question.type === 'number_input' && renderNumberInput()}
        {question.type === 'item_selection' && renderItemSelection()}
      </div>

      {error && (
        <div className="mx-6 mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

        <div className="px-6 py-4 border-t border-gray-200">
          {currentAnswer ? (
            <div className="text-center">
              <span className="text-green-600 font-semibold flex items-center justify-center gap-1">
                <IoCheckmark /> Answer submitted
              </span>
              <p className="text-sm text-gray-600 mt-1">Waiting for other players...</p>
            </div>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={disabled || !answer}
              className="w-full rounded-lg bg-[#367A25] px-6 py-3 font-semibold text-white transition-colors hover:bg-[#2d6820] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Submit Answer
            </button>
          )}
        </div>
      </div>

      {/* Post-Decision Feedback */}
      {currentAnswer && postDecisionFeedback && (
        <div className="mt-4 bg-gradient-to-r from-[#E2E5EC] to-white border border-[#CED4DE] rounded-xl shadow-sm p-6">
          <div className="flex items-start gap-3">
            <span className="text-2xl"><IoBulb /></span>
            <div className="flex-1">
              <h4 className="font-semibold text-[#12356B] mb-2">Decision Outcome</h4>
              <p className="text-gray-700 leading-relaxed">{postDecisionFeedback}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionPanel;
